import { t } from "i18next";
import { makeAutoObservable } from "mobx";
import { loadObject } from "src/common/api/apiObject";
import { onError } from "src/common/onError";
import {
  createEntity,
  deleteEntity,
} from "src/pages/EntityCardPage/apiEntityCard";
import { EntityCardStore } from "src/pages/EntityCardPage/EntityCardStore";
import { compoundEntityTableStore2 } from "src/pages/EntityFiltersPage/EntityList/compoundEntityTableStore2";
import {
  EntityFilters,
  ZEntityRow,
} from "src/pages/EntityFiltersPage/EntityList/types";
import {
  loadObjectStatesInsideRefLikeAtts,
  makeObjectStatesMap,
} from "src/pages/ManagementPage/Obj2Tab/roles/rolesApi";
import { ZEntity } from "src/types/ZEntity";

import { ZObjState } from "src/types/ZObjState";

import { DefaultTSettingsStore } from "src/pages/EntityFiltersPage/DefaultTSettingsStore";
import { TableSettingsType } from "src/pages/ManagementPage/Obj2Tab/ObjTableSettings/ObjTableSettingsTypes";

import { ChildEntitiesButton } from "src/common/attrEdit/components";
import { AttrTypeMap, loadAttrTypeMap } from "src/common/attributes";
import { ConfigManager } from "src/common/ConfigManager";
import {
  EntityTableStrategy,
  getLatestEntityTSettings,
  ZEntityTSettings,
} from "src/pages/EntityFiltersPage/EntityTableStrategy";

import {
  loadObjectAttrinbutesAll,
  makeObjectAttributesMap,
} from "src/pages/ManagementPage/objectsApi";
import { ZAttribute } from "src/types/ZAttribute";

import { ZUid } from "src/types/zUid";
import { userPreferenceKeys } from "src/common/userPreferenceKeys";
import { TableStore } from "../tables/TableStore";
import { ChildEntsActionType } from "./ChildEntities.types";
import { ChildEntsAssignStore } from "./ChildEntitiesCreators/ChildEntsAssign/ChildEntsAssignStore";
import { createTableParamsKey } from "./createTableParamsKey";
import { loadChildEntsSummary } from "./loadChildEntsSummary";
import {
  applyChildEntitiesRestriction,
  ChildEntitiesRestriction,
  makeChildEntitiesInitialValues,
  makeChildEntitiesRestrictionFilters,
} from "./childEntitiesRestriction";

export type ZEntityFiltersChild = EntityFilters & {
  parentEntityId?: ZUid;
  parentLinkAttributeId?: ZUid;
};

export type ChildEntitiesInfo = {
  objectId: ZUid;
  parentEntityId: ZUid;
  parentLinkAttributeId?: ZUid;
  childAttributeId?: ZUid;
  restriction?: ChildEntitiesRestriction;
};

export class ChildEntitiesStore {
  constructor(
    actions?: Set<ChildEntsActionType>,
    private fnCreate?: (entity: ZEntity) => Promise<ZEntity>,
  ) {
    this.availableActions =
      actions ??
      new Set([
        ChildEntsActionType.changeState,
        ChildEntsActionType.copyIn,
        ChildEntsActionType.copyFrom,
        ChildEntsActionType.select,
        ChildEntsActionType.rowClick,
      ]);
    this.tableConfig = new ConfigManager<ZEntityTSettings>(
      new EntityTableStrategy(),
    );
    makeAutoObservable(this);
  }

  protected availableActions: Set<ChildEntsActionType>;

  tableConfig: ConfigManager<ZEntityTSettings>;

  tableStore: TableStore<ZEntityRow, ZEntityFiltersChild> | null = null;

  setTableStore(
    newTableStore: TableStore<ZEntityRow, ZEntityFiltersChild> | null,
  ) {
    this.tableStore = newTableStore;
  }

  objectStates: ZObjState[] = [];

  setObjectStates(list: ZObjState[]) {
    this.objectStates = list;
  }

  defTSettingsStore = new DefaultTSettingsStore(
    TableSettingsType.childObjectId,
  );

  typesMap: AttrTypeMap = {};

  setTypesMap(value: AttrTypeMap) {
    this.typesMap = value;
  }

  attrsMap: Record<ZUid, ZAttribute> = {};

  setAttrsMap(value: Record<ZUid, ZAttribute>) {
    this.attrsMap = value;
  }

  info: ChildEntitiesInfo | null = null;

  setInfo(assinInfo: ChildEntitiesInfo) {
    this.info = assinInfo;
  }

  async init(
    objectId: ZUid,
    parentEntityId: ZUid,
    parentLinkAttributeId?: ZUid,
    childAttributeId?: ZUid,
    summaryAttrs?: ZUid[],
    buttons?: ChildEntitiesButton[],
    restriction?: ChildEntitiesRestriction,
  ) {
    const [object, typesMap, attributes] = await Promise.all([
      loadObject(objectId, { translate: true }),
      loadAttrTypeMap(),
      loadObjectAttrinbutesAll(String(objectId), { translate: true }),
    ]);
    const info: ChildEntitiesInfo = {
      objectId,
      parentEntityId,
      parentLinkAttributeId,
      childAttributeId,
      restriction,
    };
    this.setInfo(info);
    this.setCurrObjName(object.name);
    this.setTypesMap(typesMap);
    this.setAttrsMap(makeObjectAttributesMap(attributes));
    this.setSummaryAttrs(summaryAttrs ?? null);
    this.setObjectStates(await loadObjectStatesInsideRefLikeAtts(objectId));
    if (buttons) this.setButtons(buttons);

    const key = userPreferenceKeys.childEntities(objectId);
    const { data: savedParams } = await this.tableConfig.get(key);
    const savedFilters = { ...(savedParams?.filters ?? {}) };
    delete savedFilters.attributeId;
    delete savedFilters.restrictionValues;
    const defFilters: ZEntityFiltersChild = {
      objectId,
      parentEntityId,
      parentLinkAttributeId,
      ...makeChildEntitiesRestrictionFilters(restriction),
    };
    const finalFilters = {
      ...savedFilters,
      ...defFilters,
    };
    const isFirstView = !savedParams.filters?.objectId;

    await this.defTSettingsStore.init(objectId);
    const initDefSettings = () =>
      this.defTSettingsStore?.initSettings(this.tableStore, isFirstView);

    const finalParams = {
      ...(savedParams ?? this.defTSettingsStore?.sortParams),
      filters: finalFilters,
    };

    const saveParams = async () => {
      const params = this.tableStore?.params;
      if (params) {
        const filtersWithoutRestriction = { ...(params.filters ?? {}) };
        delete filtersWithoutRestriction.attributeId;
        delete filtersWithoutRestriction.restrictionValues;
        await this.tableConfig.save(
          key,
          getLatestEntityTSettings({
            ...params,
            filters: filtersWithoutRestriction,
          }),
        );
      }
    };

    this.setTableStore(
      await compoundEntityTableStore2<ZEntityRow>(
        objectId,
        "id",
        this.typesMap,
        makeObjectStatesMap(this.objectStates),
        finalParams,
        { keepSelected: true, selectionType: "checkbox" },
        async () => {
          if (summaryAttrs?.length) this.checkSummaryChange();
          await saveParams();
          initDefSettings?.();
        },
        { visibleAttrs: this.defTSettingsStore?.visibleAttrs },
      ),
    );
    this.assignStore.init(info);
  }

  tableIsInited: boolean = false;

  setTableIsInited(flag: boolean) {
    this.tableIsInited = flag;
  }

  get avalibleActionsSet() {
    return this.availableActions;
  }

  get isPopup(): boolean {
    return !!this.subCardStore;
  }

  subCardStore: EntityCardStore | null = null;

  setSubCardStore(store: EntityCardStore | null) {
    this.subCardStore = store;
  }

  submitText: string = "";

  setSubmitText(text: string) {
    this.submitText = text;
  }

  openCreation() {
    try {
      const { info, fnCreate } = this;
      if (!info) throw Error(t("Missing value", { parameter: "info" }));
      const { objectId, parentLinkAttributeId, parentEntityId, restriction } =
        info;
      if (!parentLinkAttributeId)
        throw Error(t("Missing value", { parameter: "parentLinkAttributeId" }));
      this.setSubmitText(t("Add"));
      const createFn = fnCreate ?? createEntity;
      const store = new EntityCardStore({
        fnCreate: (entity) =>
          createFn(applyChildEntitiesRestriction(entity, restriction)),
      });
      this.setSubCardStore(store);
      store.initNew(
        objectId,
        { [parentLinkAttributeId]: [String(parentEntityId)] },
        { initialIfPresent: makeChildEntitiesInitialValues(restriction) },
      );
    } catch (error) {
      onError(error);
    }
  }

  openEdit(entityId: ZUid) {
    this.setSubmitText(t("Apply"));
    const store = new EntityCardStore();
    this.setSubCardStore(store);
    store.init(String(entityId));
  }

  closeCreation() {
    this.setSubCardStore(null);
  }

  onUpdate() {
    this.tableStore
      ?.reload()
      .then(() => this.loadSummary())
      .catch(onError);
    this.closeCreation();
  }

  deletingSubEntity = false;

  setDeletingSubEntity(value: boolean) {
    this.deletingSubEntity = value;
  }

  async doDeleteSubEntity() {
    try {
      this.setDeletingSubEntity(true);
      const { subCardStore } = this;
      if (!subCardStore) return;
      const { info } = subCardStore;
      if (info.status !== "ready") return;
      const entityId = info.result.entity?.id;
      if (!entityId) return;
      await deleteEntity(entityId);
      this.onUpdate();
    } catch (e) {
      onError(e);
    } finally {
      this.setDeletingSubEntity(false);
    }
  }

  currObjName: string = t("Add");

  setCurrObjName(name: string) {
    this.currObjName = name;
  }

  // Итоги вычислений в таблице

  summaryAttrs: ZUid[] | null = null;

  setSummaryAttrs(ids: ZUid[] | null) {
    this.summaryAttrs = ids;
  }

  summaryValues: Record<ZUid, number> | null = null;

  setSummaryValues(values: Record<ZUid, number>) {
    this.summaryValues = values;
  }

  lastTableParams: string = "";

  setLastTableParams(params: string) {
    this.lastTableParams = params;
  }

  async loadSummary() {
    const { summaryAttrs, tableStore, info } = this;
    if (!tableStore || !tableStore.filters || !info) return;

    const { filters } = tableStore;
    const { objectId, parentEntityId, parentLinkAttributeId, query } = filters;
    const { childAttributeId } = info;
    if (
      !summaryAttrs?.length ||
      !objectId ||
      !parentEntityId ||
      !parentLinkAttributeId ||
      !childAttributeId
    )
      return;

    const totalValues = (
      await loadChildEntsSummary(
        objectId,
        parentEntityId,
        childAttributeId,
        parentLinkAttributeId,
        summaryAttrs,
        query,
        info.restriction,
      )
    ).reduce(
      (result, { attributeId, sum }) => ({ ...result, [attributeId]: sum }),
      {},
    );
    this.setSummaryValues(totalValues);
  }

  checkSummaryChange() {
    const { tableStore, lastTableParams } = this;
    if (!tableStore || !tableStore.filters) return;
    const { filters, totalItems } = tableStore;
    const curTableParams = createTableParamsKey(filters, totalItems);
    if (curTableParams === lastTableParams) return;
    this.loadSummary()
      .then(() => this.setLastTableParams(curTableParams))
      .catch(onError);
  }

  // Кнопки

  assignStore = new ChildEntsAssignStore(() => this.onUpdate());

  buttons: ChildEntitiesButton[] = [];

  setButtons(list: ChildEntitiesButton[]) {
    this.buttons = list;
  }

  get isDropdownView(): boolean {
    return this.buttons.length > 1;
  }
}
